import { useState } from "react";
import type { AnalysisType } from "@/types";

interface CreateAnalysisModalProps {
  sampleId: number;
  sampleName: string;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: { sample_id: number; analysis_type: AnalysisType }) => void;
  isLoading: boolean;
  /** 서버가 거절한 사유 (예: 409 "이미 분석이 진행 중입니다"). 모달 안에 그대로 보인다. */
  error?: string | null;
}

const ANALYSIS_TYPES: {
  value: AnalysisType;
  label: string;
  description: string;
}[] = [
  {
    value: "pattern_matching",
    label: "패턴 매칭",
    description: "스펙트럼 패턴을 데이터베이스와 비교하여 물질을 식별합니다.",
  },
  {
    value: "compound_inference",
    label: "화합물 추론",
    description: "AI 모델을 사용하여 미지 화합물을 추론합니다.",
  },
  {
    value: "hazard_assessment",
    label: "위험성 평가",
    description: "검출된 물질의 위험성을 종합 평가합니다.",
  },
  {
    value: "full_analysis",
    label: "전체 분석",
    description: "패턴 매칭 + 화합물 추론 + 위험성 평가를 순차 실행합니다.",
  },
];

export default function CreateAnalysisModal({
  sampleId,
  sampleName,
  isOpen,
  onClose,
  onSubmit,
  isLoading,
  error,
}: CreateAnalysisModalProps) {
  const [selectedType, setSelectedType] =
    useState<AnalysisType>("full_analysis");

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-1">분석 시작</h3>
        <p className="text-sm text-gray-500 mb-4">
          시료: <span className="font-medium text-gray-700">{sampleName}</span>{" "}
          (ID: {sampleId})
        </p>

        <div className="space-y-3 mb-6">
          {ANALYSIS_TYPES.map((type) => (
            <label
              key={type.value}
              className={`flex items-start p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                selectedType === type.value
                  ? "border-blue-500 bg-blue-50"
                  : "border-gray-200 hover:border-gray-300"
              }`}
            >
              <input
                type="radio"
                name="analysisType"
                value={type.value}
                checked={selectedType === type.value}
                onChange={() => setSelectedType(type.value)}
                className="mt-0.5 mr-3"
              />
              <div>
                <div className="font-medium text-gray-900">{type.label}</div>
                <div className="text-sm text-gray-500">{type.description}</div>
              </div>
            </label>
          ))}
        </div>

        {error ? (
          <div
            role="alert"
            className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
          >
            {error}
          </div>
        ) : null}

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            disabled={isLoading}
          >
            취소
          </button>
          <button
            type="button"
            onClick={() =>
              onSubmit({ sample_id: sampleId, analysis_type: selectedType })
            }
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
            disabled={isLoading}
          >
            {isLoading ? "분석 생성 중..." : "분석 시작"}
          </button>
        </div>
      </div>
    </div>
  );
}
